import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  codTelefonEa,
  eBucuresti,
  eRomania,
  judetEa,
  pregatestePartener,
  sectorBucuresti,
  JUDETE_EA,
  JUDET_STRAINATATE,
  SECTOARE_BUCURESTI,
} from '@b4l/shared'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'

interface AnafResult {
  found: boolean
  cui?: string | null
  denumire?: string | null
  nrRegCom?: string | null
  adresa?: string | null
  judet?: string | null
  localitate?: string | null
  codPostal?: string | null
  tva?: boolean
  error?: string
}
interface BranchForm {
  branchName: string
  address: string
  city: string
  state: string
  postcode: string
  person: string
  tel: string
}
const emptyBranch = (): BranchForm => ({
  branchName: '',
  address: '',
  city: '',
  state: '',
  postcode: '',
  person: '',
  tel: '',
})

/** Erorile pe campuri, cu cheile din `pregatestePartener` (aceleasi si in raspunsul API-ului). */
type Erori = Partial<Record<string, string>>

/** Ce s-a intamplat in EA la salvare — vezi `createPartner`. */
type EaLaCreare = 'trimis' | 'exista' | 'netrimis' | 'respins'

const SELECT =
  'w-full rounded border border-b4l-border px-3 py-2 outline-none focus:border-b4l-green'

const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
    ?.message || 'Eroare necunoscută'

/** Ce vine deja completat (din ANAF sau din textul căutat), la deschiderea ferestrei. */
export interface PrecompletarePartener {
  /** De unde vin datele (doar la furnizor: `anaf`, `istoric`, `manual`…). */
  sursa?: string
  tara?: string
  cif?: string
  name?: string
  regCom?: string
  address?: string
  city?: string
  county?: string
  postcode?: string
  vatPayer?: boolean | null
}

/** Câmpurile cu eroare afișată lângă ele (cheile din `pregatestePartener`). */
const CAMPURI_VIZIBILE = new Set([
  'denumire',
  'codFiscal',
  'regCom',
  'tara',
  'judet',
  'localitate',
  'adresa',
  'email',
  'telefon',
])

const GRUPURI_CLIENT = [
  { value: 'B2B', label: 'B2B' },
  { value: 'B2C', label: 'B2C' },
]

export function AddClientModal({
  open,
  onClose,
  onCreated,
  endpoint,
  precompletare,
  grupuri = GRUPURI_CLIENT,
  grupImplicit = 'B2B',
  rol = 'client',
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: number, nume: string) => void
  /**
   * Unde se salvează: fiecare ecran are ruta lui, cu drepturile lui (vezi modules/parteneri).
   * Implicit `/orders/partners` la client și `/furnizori` la furnizor.
   */
  endpoint?: string
  precompletare?: PrecompletarePartener | null
  grupuri?: { value: string; label: string }[]
  grupImplicit?: string
  /**
   * `furnizor`: aceeași fereastră, fără ce ține doar de client (persoană fizică, grup, termen,
   * contacte, puncte de lucru); se salvează în nomenclatorul de furnizori (`POST /furnizori`).
   * La un furnizor din altă țară codul de TVA e opțional (Alexandru, 17.09.2026).
   */
  rol?: 'client' | 'furnizor'
}) {
  const furnizor = rol === 'furnizor'
  const [cif, setCif] = useState('')
  const [name, setName] = useState('')
  const [regCom, setRegCom] = useState('')
  /**
   * Asociatie, fundatie: nu e la Registrul Comertului, deci n-are numar J (Dorian, 30.10.2026). EA
   * cere totusi campul la creare; primeste `J`, marcajul de organizatie — ca la firmele straine.
   */
  const [faraRegCom, setFaraRegCom] = useState(false)
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [county, setCounty] = useState('')
  const [postcode, setPostcode] = useState('')
  const [legalRep, setLegalRep] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [iban, setIban] = useState('')
  const [swift, setSwift] = useState('')
  const [remarks, setRemarks] = useState('')
  const [grup, setGrup] = useState(grupImplicit)
  const [paymTermDays, setPaymTermDays] = useState('')
  const [vatPayer, setVatPayer] = useState<boolean | null>(null)
  const [tipPersoana, setTipPersoana] = useState<'juridica' | 'fizica'>('juridica')
  const [branches, setBranches] = useState<BranchForm[]>([])
  const [cifError, setCifError] = useState('')
  const [tara, setTara] = useState('Romania')
  const [erori, setErori] = useState<Erori>({})

  const fizica = tipPersoana === 'fizica'
  // Firma din alta tara: judetul e `-Strainatate-` si Nr. Reg. Com. `J` — le pune regula.
  const strain = !fizica && !eRomania(tara)
  const bucuresti = !strain && eBucuresti(county)
  const regComTrimis = strain || faraRegCom ? 'J' : regCom
  // La persoana fizica nu se tasteaza cod fiscal: se formeaza din telefon, in conventia EA.
  const codFizica = codTelefonEa(phone)

  const reset = () => {
    setCif('')
    setVatPayer(null)
    setTipPersoana('juridica')
    setName('')
    setRegCom('')
    setFaraRegCom(false)
    setAddress('')
    setCity('')
    setCounty('')
    setPostcode('')
    setLegalRep('')
    setContactPerson('')
    setContactEmail('')
    setPhone('')
    setIban('')
    setSwift('')
    setGrup(grupImplicit)
    setPaymTermDays('')
    setBranches([])
    setCifError('')
    setTara('Romania')
    setErori({})
  }

  useEffect(() => {
    if (!open) return
    // Bifa nu ramane de la deschiderea trecuta (fereastra nu se demonteaza): o decide ce vine acum —
    // bifata doar cand datele vin din ANAF fara numar la ONRC.
    setFaraRegCom(precompletare?.sursa === 'anaf' && !/\d/.test(precompletare.regCom ?? ''))
    if (!precompletare) return
    const p = precompletare
    if (p.cif !== undefined) setCif(p.cif)
    if (p.name !== undefined) setName(p.name)
    if (p.regCom !== undefined) setRegCom(p.regCom)
    if (p.address !== undefined) setAddress(p.address)
    if (p.postcode !== undefined) setPostcode(p.postcode)
    if (p.vatPayer !== undefined) setVatPayer(p.vatPayer)
    if (p.tara !== undefined) setTara(p.tara)
    const j = judetEa(p.county)
    if (j) setCounty(j)
    if (p.city !== undefined) setCity((eBucuresti(j) && sectorBucuresti(p.city)) || p.city)
  }, [open, precompletare])

  const anafMut = useMutation({
    mutationFn: async () =>
      (await api.get<{ result: AnafResult }>(`/orders/anaf-lookup?cif=${encodeURIComponent(cif)}`))
        .data.result,
    onSuccess: (r) => {
      if (!r.found) {
        toast.error(r.error || 'Firma nu a fost găsită în ANAF. Completează manual.')
        return
      }
      if (r.denumire) setName(r.denumire)
      // Fara numar la ONRC (asociatie, fundatie): bifa se pune singura; cu numar, se scoate.
      const areNr = /\d/.test(r.nrRegCom ?? '')
      if (areNr) setRegCom(r.nrRegCom!)
      setFaraRegCom(!areNr)
      if (r.adresa) setAddress(r.adresa)
      // Judetul in forma EA; in Bucuresti localitatea e sectorul („Bucuresti Sectorul 3" → „Sector 3").
      const j = judetEa(r.judet)
      if (j) setCounty(j)
      if (r.localitate) setCity((eBucuresti(j) && sectorBucuresti(r.localitate)) || r.localitate)
      if (r.codPostal) setPostcode(r.codPostal)
      // Cod fiscal canonic: cu prefix RO daca e platitoare de TVA, fara daca nu e.
      const digits = (r.cui || cif).replace(/^RO/i, '').replace(/\s/g, '')
      setVatPayer(r.tva ?? null)
      setCif((r.tva ? 'RO' : '') + digits)
      toast.success('Date completate din ANAF')
    },
    onError: (e) => toast.error('ANAF: ' + errMsg(e)),
  })

  const saveFurnizor = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          furnizor: {
            id: number
            nume: string
            ea: 'trimis' | 'exista deja' | 'neconfigurat' | 'esuat'
            eaMesaj?: string
          }
        }>(endpoint ?? '/furnizori', {
          nume: name,
          cif: cif.trim() || null,
          tara,
          regCom: regComTrimis || null,
          adresa: address || null,
          oras: city || null,
          judet: strain ? JUDET_STRAINATATE : county || null,
          platitorTva: vatPayer,
          sursa: precompletare?.sursa ?? 'crm',
        })
      ).data.furnizor,
    onSuccess: (f) => {
      if (f.ea === 'trimis') toast.success(`„${f.nume}" a fost creat și trimis în ExpertAccounts.`)
      else if (f.ea === 'exista deja')
        toast.success(`„${f.nume}" a fost adăugat. În ExpertAccounts exista deja.`)
      else if (f.ea === 'neconfigurat')
        toast.warning(
          `„${f.nume}" e creat în CRM; ExpertAccounts nu e configurat, pleacă singur după configurare.`,
          { duration: 12000 },
        )
      else
        toast.warning(
          `„${f.nume}" e creat în CRM, dar ExpertAccounts l-a refuzat: ${f.eaMesaj ?? 'fără detalii'}`,
          { duration: 15000 },
        )
      reset()
      onCreated(f.id, f.nume)
    },
    onError: (e) => laEroare(e),
  })

  function laEroare(e: unknown) {
    const msg = errMsg(e)
    const resp = (
      e as {
        response?: { status?: number; data?: { error?: { fields?: Record<string, string[]> } } }
      }
    ).response
    const campuri = resp?.data?.error?.fields
    if (resp?.status === 400 && campuri) {
      setErori(Object.fromEntries(Object.entries(campuri).map(([k, v]) => [k, v[0]])))
      // Un câmp pe care fereastra nu-l arată: mesajul întreg, altfel omul n-ar ști ce lipsește.
      const ascunse = Object.keys(campuri).filter((k) => !CAMPURI_VIZIBILE.has(k))
      toast.error(
        ascunse.length
          ? `${msg}: ${ascunse.map((k) => campuri[k][0]).join(' ')}`
          : 'Completează câmpurile marcate cu roșu.',
      )
    } else if (resp?.status === 409) setCifError(msg)
    else toast.error(msg)
  }

  const saveMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ id: number; ea: EaLaCreare; eaRaspuns?: string | null }>(
          endpoint ?? '/orders/partners',
          {
            name,
            tipPersoana,
            country: fizica ? 'Romania' : tara,
            cif: fizica ? null : cif,
            vatPayer: fizica ? null : vatPayer,
            regCom: regComTrimis || null,
            address: address || null,
            city: city || null,
            county: strain ? JUDET_STRAINATATE : county || null,
            postcode: postcode || null,
            legalRep: legalRep || null,
            contactPerson: contactPerson || null,
            contactEmail: contactEmail || null,
            phone: phone || null,
            iban: iban || null,
            swift: swift || null,
            remarks: remarks || null,
            grup: grup || null,
            paymTermDays: paymTermDays.trim() ? Number(paymTermDays) : null,
            branches: branches.filter((b) => b.branchName.trim()),
          },
        )
      ).data,
    onSuccess: (r) => {
      if (r.ea === 'trimis') toast.success('Client creat și trimis în ExpertAccounts.')
      else if (r.ea === 'exista')
        toast.success('Client creat. Era deja în ExpertAccounts — l-am legat de fișa de acolo.')
      else if (r.ea === 'respins')
        toast.warning(
          'Client creat în CRM, dar ExpertAccounts l-a refuzat: ' + (r.eaRaspuns || 'fără motiv'),
          { duration: 15000 },
        )
      else
        toast.warning(
          'Client creat în CRM. ExpertAccounts nu a răspuns acum — se retrimite automat, din oră în oră.',
          { duration: 10000 },
        )
      reset()
      onCreated(r.id, name)
    },
    onError: (e) => laEroare(e),
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={furnizor ? 'Adaugă furnizor nou' : 'Adaugă client nou'}
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button
            onClick={() => {
              setCifError('')
              const r = pregatestePartener(
                {
                  tip: tipPersoana,
                  denumire: name,
                  codFiscal: cif,
                  platitorTva: vatPayer,
                  regCom: regComTrimis,
                  tara: fizica ? 'Romania' : tara,
                  judet: county,
                  localitate: city,
                  adresa: address,
                  telefon: furnizor ? null : phone,
                  email: furnizor ? null : contactEmail,
                },
                { codOptionalStrain: furnizor },
              )
              if (furnizor && !tara.trim()) {
                // Registrul de furnizori e pentru firme din afara României: țara nu se presupune.
                setErori({ ...(r.ok ? {} : r.erori), tara: 'Alege țara.' })
                return
              }
              // Furnizor românesc cu CUI: ce lipsește din sediu completează serverul din ANAF
              // (ca la butoanele dintr-un clic); dacă tot lipsește, revine cu erori pe câmpuri.
              const dinAnaf = ['regCom', 'adresa', 'judet', 'localitate']
              const blocante = r.ok
                ? {}
                : furnizor && !strain && /^\s*(ro)?\s*\d{2,10}\s*$/i.test(cif)
                  ? Object.fromEntries(
                      Object.entries(r.erori).filter(([k]) => !dinAnaf.includes(k)),
                    )
                  : r.erori
              if (Object.keys(blocante).length) {
                setErori(blocante)
                return
              }
              setErori({})
              if (furnizor) saveFurnizor.mutate()
              else saveMut.mutate()
            }}
            loading={saveMut.isPending || saveFurnizor.isPending}
            disabled={!name.trim() || (fizica ? !codFizica : !cif.trim() && !(furnizor && strain))}
          >
            {furnizor ? 'Salvează furnizor' : 'Salvează client'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className={furnizor ? 'hidden' : 'flex gap-2'}>
          {(['juridica', 'fizica'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTipPersoana(t)
                setCifError('')
                setErori({})
                if (grupuri.some((g) => g.value === 'B2C' || g.value === 'B2B'))
                  setGrup(t === 'fizica' ? 'B2C' : grupImplicit)
              }}
              className={`flex-1 rounded border px-3 py-2 text-sm ${
                tipPersoana === t
                  ? 'border-b4l-green bg-b4l-green/10 font-medium text-b4l-green'
                  : 'border-b4l-border text-b4l-text-muted hover:bg-gray-50'
              }`}
            >
              {t === 'juridica' ? 'Persoană juridică' : 'Persoană fizică'}
            </button>
          ))}
        </div>

        {fizica ? (
          <div className="rounded border border-b4l-border bg-gray-50 px-3 py-2 text-xs text-b4l-text-muted">
            Codul fiscal se completează automat din telefon, în forma cerută de ExpertAccounts:{' '}
            <span className="font-mono text-b4l-text">{codFizica || 'Tel.0745123456'}</span>
            {!codFizica && phone.trim() ? (
              <span className="text-red-600"> — număr de telefon invalid</span>
            ) : null}
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <Field
              label={
                furnizor
                  ? strain
                    ? 'Cod de TVA (opțional)'
                    : 'Cod fiscal (CUI)'
                  : 'Cod fiscal (CUI)'
              }
              value={cif}
              onChange={(e) => {
                setCif(e.target.value)
                setCifError('')
              }}
              error={cifError || erori.codFiscal}
              placeholder="Introdu codul fiscal (ex. RO22153340), apoi apasă butonul de căutare"
              className="flex-1"
            />
            <Button
              variant="secondary"
              onClick={() => anafMut.mutate()}
              loading={anafMut.isPending}
              disabled={!cif.trim()}
            >
              Caută în ANAF
            </Button>
          </div>
        )}

        <Field
          label={fizica ? 'Nume și prenume' : 'Denumire'}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={erori.denumire}
        />

        <div className={furnizor ? 'hidden' : 'grid gap-3 sm:grid-cols-2'}>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Grup</label>
            <select
              value={grup}
              onChange={(e) => setGrup(e.target.value)}
              className="w-full rounded border border-b4l-border px-3 py-2 outline-none focus:border-b4l-green"
            >
              {grupuri.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Termen de plată (zile)"
            type="number"
            min={0}
            value={paymTermDays}
            onChange={(e) => setPaymTermDays(e.target.value)}
            placeholder="ex. 30"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {fizica ? null : (
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Țara</label>
              <input
                list="tari-partener"
                value={tara}
                onChange={(e) => setTara(e.target.value)}
                className={SELECT}
                placeholder="ex. Irlanda"
              />
              {erori.tara && <p className="mt-1 text-xs text-b4l-red">{erori.tara}</p>}
              <datalist id="tari-partener">
                {[
                  'Romania',
                  'Germania',
                  'Irlanda',
                  'Italia',
                  'Franta',
                  'Ungaria',
                  'Bulgaria',
                  'Austria',
                  'Olanda',
                  'Polonia',
                  'Marea Britanie',
                  'Statele Unite',
                  'Cipru',
                ].map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
          )}
          {fizica ? null : (
            <div>
              <Field
                label="Nr. Reg. Comerț"
                value={strain || faraRegCom ? 'J' : regCom}
                onChange={(e) => setRegCom(e.target.value)}
                disabled={strain || faraRegCom}
                error={erori.regCom}
                placeholder="ex. J40/1234/2015"
              />
              {!strain && (
                <label className="mt-1.5 flex items-center gap-2 text-xs text-b4l-text-muted">
                  <input
                    type="checkbox"
                    checked={faraRegCom}
                    onChange={(e) => {
                      setFaraRegCom(e.target.checked)
                      setErori(({ regCom: _, ...rest }) => rest)
                    }}
                  />
                  Nu e la Registrul Comerțului (asociație, fundație)
                </label>
              )}
            </div>
          )}
        </div>
        <Field
          label="Adresă (stradă și număr)"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          error={erori.adresa}
          placeholder="ex. Str. Memorandumului nr. 1"
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Județ</label>
            {strain ? (
              <input value={JUDET_STRAINATATE} disabled className={SELECT + ' bg-gray-50'} />
            ) : (
              <select
                value={judetEa(county) ?? ''}
                onChange={(e) => {
                  setCounty(e.target.value)
                  // Schimbarea din/spre Bucuresti schimba felul localitatii (sector / text liber).
                  if (eBucuresti(e.target.value) !== eBucuresti(county)) setCity('')
                }}
                className={SELECT}
              >
                <option value="">— alege județul —</option>
                {JUDETE_EA.map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
            )}
            {erori.judet && <p className="mt-1 text-xs text-b4l-red">{erori.judet}</p>}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              {bucuresti ? 'Sector' : 'Localitate'}
            </label>
            {bucuresti ? (
              <select
                value={sectorBucuresti(city) ?? ''}
                onChange={(e) => setCity(e.target.value)}
                className={SELECT}
              >
                <option value="">— alege sectorul —</option>
                {SECTOARE_BUCURESTI.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            ) : (
              <input value={city} onChange={(e) => setCity(e.target.value)} className={SELECT} />
            )}
            {erori.localitate && <p className="mt-1 text-xs text-b4l-red">{erori.localitate}</p>}
          </div>
          {furnizor ? null : (
            <Field
              label="Cod poștal"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
            />
          )}
        </div>

        {furnizor ? null : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {fizica ? null : (
                <>
                  <Field
                    label="Reprezentant legal"
                    value={legalRep}
                    onChange={(e) => setLegalRep(e.target.value)}
                  />
                  <Field
                    label="Persoană de contact"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                  />
                </>
              )}
              <Field
                label="Email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                error={erori.email}
              />
              <Field
                label={fizica ? 'Telefon (formează codul fiscal)' : 'Telefon'}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value)
                  setCifError('')
                }}
                error={fizica ? cifError || erori.telefon : ''}
              />
              <Field label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} />
              <Field label="SWIFT" value={swift} onChange={(e) => setSwift(e.target.value)} />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
                Observații partener
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                placeholder="Ajung pe fișa partenerului în ExpertAccounts"
                className="w-full rounded border border-b4l-border px-3 py-2 outline-none focus:border-b4l-green"
              />
            </div>

            <div className="border-t pt-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Puncte de lucru (livrare)</span>
                <Button
                  variant="secondary"
                  onClick={() => setBranches((b) => [...b, emptyBranch()])}
                >
                  + Adaugă punct
                </Button>
              </div>
              {branches.map((b, i) => (
                <div key={i} className="mb-3 rounded border border-gray-200 p-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field
                      label="Nume punct"
                      value={b.branchName}
                      onChange={(e) =>
                        setBranches((arr) =>
                          arr.map((x, j) => (j === i ? { ...x, branchName: e.target.value } : x)),
                        )
                      }
                    />
                    <Field
                      label="Adresă livrare"
                      value={b.address}
                      onChange={(e) =>
                        setBranches((arr) =>
                          arr.map((x, j) => (j === i ? { ...x, address: e.target.value } : x)),
                        )
                      }
                    />
                    <Field
                      label="Localitate"
                      value={b.city}
                      onChange={(e) =>
                        setBranches((arr) =>
                          arr.map((x, j) => (j === i ? { ...x, city: e.target.value } : x)),
                        )
                      }
                    />
                    <Field
                      label="Județ"
                      value={b.state}
                      onChange={(e) =>
                        setBranches((arr) =>
                          arr.map((x, j) => (j === i ? { ...x, state: e.target.value } : x)),
                        )
                      }
                    />
                    <Field
                      label="Persoană"
                      value={b.person}
                      onChange={(e) =>
                        setBranches((arr) =>
                          arr.map((x, j) => (j === i ? { ...x, person: e.target.value } : x)),
                        )
                      }
                    />
                    <Field
                      label="Telefon"
                      value={b.tel}
                      onChange={(e) =>
                        setBranches((arr) =>
                          arr.map((x, j) => (j === i ? { ...x, tel: e.target.value } : x)),
                        )
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="mt-2 text-xs text-red-600 hover:underline"
                    onClick={() => setBranches((arr) => arr.filter((_, j) => j !== i))}
                  >
                    Șterge punctul
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
